import {Group, StackProps, type Sx, Text, useMantineTheme} from '@mantine/core';
import React, {PropsWithChildren, ReactNode} from 'react';

import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {multiLineEllipsisText} from 'styles/Utilities';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {Header} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeHeader';
import {
  BenchlingNodeV2Status,
  STATUS_COLORS,
} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeV2Constants';
import {useBenchlingNodeV2Context} from 'ui-library/reactflow/nodes/BenchlingNode/v2/useBenchlingNodeV2Context';

const NODE_HEADER_ICON_NAMES: Record<BenchlingNodeV2Status, BenchlingIconName> = {
  error: 'circle-xmark',
  'in-progress': 'timer-outline',
  pending: 'circle-outline',
  success: 'check-circle',
  warning: 'exclamation-triangle',
};

export interface StandardHeaderProps {
  /** A title for the header. This is displayed in large, black text at the top of the header. */
  title: string;
  /** A subtitle for the header. This is displayed in small, gray text at the bottom of the header. */
  subTitle?: string;
  /** An icon to be displayed in the top left corner of the header. This icon is always colored gray. */
  leftIcon?: BenchlingIconName;
  /**
   * An icon to be displayed in the top right corner of the header. This icon always takes the node's status
   * color.
   * @default An icon corresponding with the node status
   */
  rightIcon?: BenchlingIconName;
  /** A ReactFlow handle to display on the left-hand side of this header. */
  leftHandle?: ReactNode;
  /** A ReactFlow handle to display on the right-hand side of this header. */
  rightHandle?: ReactNode;
}

/**
 * A standard header for a {@link BenchlingNodeV2}. It consumes the context provided by
 * {@link BenchlingNodeV2}, and as such automatically handles node status.
 * @package
 */
export function StandardHeader(
  props: PropsWithChildren<StandardHeaderProps & Omit<StackProps, 'children'>>
): JSX.Element {
  const {status} = useBenchlingNodeV2Context();
  const theme = useMantineTheme();

  const {leftIcon, rightIcon, leftHandle, rightHandle, title, subTitle, ...stackProps} = props;
  let calculatedRightIcon: BenchlingIconName | undefined;
  if (rightIcon == null) {
    if (status != null) {
      calculatedRightIcon = NODE_HEADER_ICON_NAMES[status];
    }
  } else {
    calculatedRightIcon = rightIcon;
  }

  return (
    <Header {...stackProps}>
      {leftHandle}
      <Group spacing="sm" align="flex-start">
        {leftIcon && (
          <BenchlingIcon icon={leftIcon} style={{color: theme.colors.gray[6], height: 16, width: 16}} />
        )}
        <Text
          fw={FONT_WEIGHT_PROXIMA_SEMIBOLD}
          fz="md"
          sx={{
            flex: 1,
            ...(multiLineEllipsisText(2) as Sx),
          }}
        >
          {title}
        </Text>
        {calculatedRightIcon && (
          <BenchlingIcon
            icon={calculatedRightIcon}
            style={{
              color:
                theme.colors[STATUS_COLORS[status ?? 'default'].color][
                  STATUS_COLORS[status ?? 'default'].colorIndex
                ],
              height: 16,
              width: 16,
            }}
          />
        )}
      </Group>
      {subTitle && (
        <Text
          c="gray.6"
          fz="sm"
          sx={{
            ...(multiLineEllipsisText(2) as Sx),
          }}
        >
          {subTitle}
        </Text>
      )}
      {rightHandle}
    </Header>
  );
}
